import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { formatNumber } from '@/utils/format';

/**
 * URL-persisted list state for recruit registers (filters survive refresh, back and shared links).
 * `filterKeys` are read from / written to the query string alongside page, limit, q and sort.
 */
export function useRecruitList<K extends string>(filterKeys: readonly K[], defaultLimit = 25) {
  const [sp, setSp] = useSearchParams();

  const page = Math.max(1, Number(sp.get('page')) || 1);
  const limit = Math.min(200, Math.max(10, Number(sp.get('limit')) || defaultLimit));
  const q = sp.get('q') || '';
  const sortBy = sp.get('sortBy') || undefined;
  const sortDir = (sp.get('sortDir') as 'ASC' | 'DESC' | null) || undefined;

  const filters = useMemo(() => {
    const out = {} as Record<K, string | undefined>;
    for (const k of filterKeys) out[k] = sp.get(k) || undefined;
    return out;
  }, [sp, filterKeys]);

  const update = useCallback(
    (patch: Record<string, string | number | undefined | null>, resetPage = true) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === '') next.delete(k);
            else next.set(k, String(v));
          }
          if (resetPage && !('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setSp]
  );

  const setFilter = useCallback((key: K, value: string | undefined | null) => update({ [key]: value }), [update]);
  const setQ = useCallback((value: string) => update({ q: value.trim() || undefined }), [update]);
  const reset = useCallback(() => setSp(new URLSearchParams(), { replace: true }), [setSp]);

  const onTableChange = useCallback(
    (p: TablePaginationConfig, _f: unknown, sorter: SorterResult<any> | SorterResult<any>[]) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      update(
        {
          page: p.current && p.current > 1 ? p.current : undefined,
          limit: p.pageSize && p.pageSize !== defaultLimit ? p.pageSize : undefined,
          sortBy: s?.order ? String(s.columnKey || s.field) : undefined,
          sortDir: s?.order ? (s.order === 'ascend' ? 'ASC' : 'DESC') : undefined,
        },
        false
      );
    },
    [update, defaultLimit]
  );

  /** Params for the paged API call. */
  const apiParams = useMemo(
    () => ({ page, limit, q: q || undefined, sortBy, sortDir, ...filters }),
    [page, limit, q, sortBy, sortDir, filters]
  );

  const pagination = (total: number): TablePaginationConfig => ({
    current: page,
    pageSize: limit,
    total,
    showSizeChanger: true,
    pageSizeOptions: [25, 50, 100, 200],
    showTotal: (t) => `${formatNumber(t)} record(s)`,
  });

  const sortOrderFor = (key: string) =>
    sortBy === key ? (sortDir === 'ASC' ? ('ascend' as const) : ('descend' as const)) : null;

  const activeFilterCount = filterKeys.filter((k) => filters[k]).length + (q ? 1 : 0);

  return { page, limit, q, filters, setFilter, setQ, reset, update, apiParams, onTableChange, pagination, sortOrderFor, activeFilterCount };
}
